/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import { useSelector } from "react-redux";
import { SidebarNavItem, SidebarProps } from "@advapacs/common";
import NavHeader from "./NavHeader";
import NavDivider from "./NavDivider";
import NavGroup from "./NavGroup";
import NavItem from "./NavItem";
import ShowForPermissions from "../ShowForPermissions";
import { IRootState } from "../../config/reducers";
import { UserHeaderMenu } from "../../parts/Header";
import { sidebarAction } from "../../reducers/slices/sidebarSlice";
import { useAppDispatch } from "../../config/store";
import { isIndividual } from "../../utils/utils";

const Bar: FunctionComponent<SidebarProps> = (props: SidebarProps) => {
  const dispatch = useAppDispatch();
  const { expand, unfold } = useSelector(
    (state: IRootState) => state.sidebarReducer
  );
  const { isMobile } = useSelector((state: IRootState) => state.layoutSlice);
  const {
    toggleSidebar,
    items,
    expandGroup,
    activeGroup,
    selectItem,
    activeItem,
  } = props;

  const checkMobileToggle = useCallback(() => {
    if (isMobile) {
      toggleSidebar && toggleSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const [seconds, setSeconds] = useState<number>(0);

  let interval: NodeJS.Timeout;
  useEffect(() => {
    if (seconds === 1) {
      dispatch(sidebarAction.toggleSidebarUnfold(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  const handleMouseEvent = useCallback(() => {
    if (!expand) {
      clearInterval(interval);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      interval = setInterval(() => {
        setSeconds(1);
      }, 250);
    }
  }, [expand]);
  const handleMouseBlur = useCallback(() => {
    clearInterval(interval);
    if (!expand) {
      setSeconds(0);
      dispatch(sidebarAction.toggleSidebarUnfold(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expand]);
  return (
    <div
      onMouseLeave={handleMouseBlur}
      onBlur={() => {}}
      onFocus={() => {}}
      onMouseEnter={handleMouseEvent}
      className={`cp-sidebar sidebar sidebar-dark sidebar-main sidebar-expand-md ${
        expand ? "sidebar-mobile-expanded" : "sidebar-main-resized"
      } ${unfold ? "sidebar-main-unfold" : ""}`}
    >
      <div className="sidebar-content">
        {isMobile && (
          <div className="sidebar-section sidebar-user my-1">
            <div className="sidebar-section-body">
              <div className="media">
                <div className="media-body">
                  <div className="font-weight-semibold">
                    <UserHeaderMenu isSidebar />
                  </div>
                </div>

                <div className="ml-3 align-self-center">
                  <button
                    onClick={() => dispatch(sidebarAction.toggle(false))}
                    type="button"
                    className="btn btn-outline-light-100 text-white border-transparent btn-icon rounded-pill btn-sm sidebar-mobile-main-toggle d-lg-none"
                  >
                    <i className="icon-cross2" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card-sidebar-mobile">
          <ul className="nav nav-sidebar" data-nav-type="accordion">
            {items?.map((item: SidebarNavItem) => {
              if (item?.type === "item" && item.show) {
                return (
                  <ShowForPermissions
                    key={item?.title}
                    can={isIndividual() ? undefined : item?.can}
                    allowVisibleToOwner={item?.allowVisibleToOwner}
                  >
                    <NavItem
                      onLinkClick={checkMobileToggle}
                      activeItem={activeItem}
                      selectItem={selectItem}
                      {...item}
                    />
                  </ShowForPermissions>
                );
              }

              if (
                (item.type === "group" && item.show) ||
                item?.href === "/Admin"
              ) {
                return (
                  <ShowForPermissions
                    key={item?.title}
                    can={isIndividual() ? undefined : item?.can}
                    allowVisibleToOwner={item?.allowVisibleToOwner}
                  >
                    <NavGroup
                      expandGroup={expandGroup}
                      selectItem={selectItem}
                      toggleSidebar={checkMobileToggle}
                      activeGroup={activeGroup}
                      activeItem={activeItem}
                      navs={item.navs}
                      href={item.href}
                      icon={item.icon}
                      title={item.title}
                      key={item.title}
                      allowVisibleToOwner={item.allowVisibleToOwner}
                    />
                  </ShowForPermissions>
                );
              }

              if (item.type === "header") {
                return <NavHeader key={uuid()} {...item} />;
              }

              if (item.type === "divider") {
                return <NavDivider key={uuid()} />;
              }

              return null;
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Bar;
